/**
 * Social link platform picker — a small popover that lets the user choose
 * a platform after clicking "Add social icon after" in the element context
 * menu. Renders an 8-tile grid populated from `getSocialPlatforms()`.
 *
 * Built on the shared openPopoverMenu primitive (surface, focus trap, outside-
 * click / iframe / Escape dismissal, viewport clamping). It keeps a bespoke 2D
 * grid keyboard nav (`disableArrowNav`), so it stays a `role="dialog"` tile
 * grid rather than a linear menu.
 *
 * Public API:
 *   openSocialLinkPicker(rect, { onPick, onClose })
 *     - rect: { top, left, width, height } in iframe viewport coords
 *     - onPick(platformId): called when user selects a platform; picker auto-closes
 *     - onClose(): called when picker closes for any reason (incl. after onPick)
 *   closeSocialLinkPicker(): force-close if open
 */

import { getSocialPlatforms } from '../editing/adapters/social-link-adapter.js';
import { reportError } from '../core/report-error.js';
import { openPopoverMenu } from './popover-menu.js';

let _activeHandle = null;

export function closeSocialLinkPicker() {
  _activeHandle?.();
}

export function openSocialLinkPicker(rect, opts) {
  closeSocialLinkPicker();

  const platforms = getSocialPlatforms();
  const iframe = document.getElementById('pb-preview');
  if (platforms.length === 0 || !iframe) {
    // Library not ready or preview missing — nothing to show.
    if (typeof opts?.onClose === 'function') opts.onClose();
    return () => {};
  }

  // Translate the iframe-viewport rect into parent-window coordinates.
  const iframeRect = iframe.getBoundingClientRect();
  const r = rect || { top: 0, left: 0, width: 0, height: 0 };
  const anchorLeft = iframeRect.left + Number(r.left || 0);
  const anchorTop = iframeRect.top + Number(r.top || 0);
  const anchorRect = {
    left: anchorLeft,
    right: anchorLeft,
    top: anchorTop,
    bottom: anchorTop + Number(r.height || 0),
    width: Number(r.width || 0),
    height: Number(r.height || 0)
  };

  const tiles = [];
  const GRID_COLS = 4;

  const handle = openPopoverMenu(null, {
    owner: 'social-link-picker',
    anchorRect,
    align: 'left',
    width: 260,
    role: 'dialog',
    menuLabel: 'Choose social platform',
    className: 'pb-social-picker',
    disableArrowNav: true, // bespoke 2D grid nav below
    render: ({ close }) => {
      const frag = document.createDocumentFragment();

      const heading = document.createElement('div');
      heading.className = 'pb-social-picker-heading';
      heading.textContent = 'Choose platform';
      frag.appendChild(heading);

      const grid = document.createElement('div');
      grid.className = 'pb-social-picker-grid';
      for (const p of platforms) {
        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'pb-social-picker-tile';
        tile.setAttribute('data-platform', p.id);
        tile.setAttribute('aria-label', p.label);
        tile.dataset.tooltip = p.label;
        tile.innerHTML = p.svgMarkup;
        const lbl = document.createElement('span');
        lbl.className = 'pb-social-picker-tile-label';
        lbl.textContent = p.label;
        tile.appendChild(lbl);
        tile.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = p.id;
          close();
          if (typeof opts?.onPick === 'function') {
            try {
              opts.onPick(id);
            } catch (err) {
              reportError('social-link-picker', err, {
                user: 'toast',
                message: 'Could not add that social icon.'
              });
            }
          }
        });
        grid.appendChild(tile);
        tiles.push(tile);
      }

      // 2D grid navigation (openPopoverMenu's linear arrow nav is disabled).
      grid.addEventListener('keydown', (e) => {
        const idx = tiles.indexOf(document.activeElement);
        if (idx === -1) return;
        let next;
        if (e.key === 'ArrowRight') next = (idx + 1) % tiles.length;
        else if (e.key === 'ArrowLeft') next = (idx - 1 + tiles.length) % tiles.length;
        else if (e.key === 'ArrowDown') next = Math.min(tiles.length - 1, idx + GRID_COLS);
        else if (e.key === 'ArrowUp') next = Math.max(0, idx - GRID_COLS);
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tiles.length - 1;
        else return;
        e.preventDefault();
        tiles[next]?.focus({ preventScroll: true });
      });

      frag.appendChild(grid);
      return frag;
    },
    onClose: () => {
      _activeHandle = null;
      if (typeof opts?.onClose === 'function') {
        try {
          opts.onClose();
        } catch (err) {
          reportError('social-link-picker', err, { user: 'silent' });
        }
      }
    }
  });

  _activeHandle = handle;
  return handle;
}
